import { Formik } from 'formik';
import { isEmpty } from 'lodash';
import React, { useState, useEffect } from 'react';
import Form, { FormElement, Label } from '@dashboard/components/Form';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import { TextInput } from '@dashboard/components/Formik';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useUserQueries from '@dashboard/hooks/queries/useUserQueries';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import { useMutation } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import { useToast } from '@dashboard/context/ToastContext';
import Button from '@dashboard/components/Button';
import InlineAlert from '@dashboard/components/InlineAlert';

const EmailForm = () => {
  const { user, setUser, token } = useAuth();
  const { emitToast } = useToast();
  const [pendingEmailChange, setPendingEmailChange] = useState(user.pendingEmail);
  const { updateUser } = useUserQueries();

  // Update pendingEmailChange whenever user changes
  useEffect(() => {
    if (user && user.pendingEmail) {
      setPendingEmailChange(user.pendingEmail);
    }
  }, [user]);

  const resendActivationEmailMutation = useMutation({
    mutationFn: () =>
      fetcher(`/users/${user._id}/resend-activation-email`, token, {
        method: 'POST',
      }),
    onSuccess: () => {
      emitToast({
        title: 'Activation email sent',
        type: 'success',
      });
    },
    onError: () => {
      emitToast({
        title: 'Unable to resend activation email',
        description: 'Please try again or contact support',
        type: 'error',
      });
    },
  });

  return (
    <Formik
      enableReinitialize
      initialValues={{
        email: user.email,
      }}
      onSubmit={async (values, actions) => {
        // Only proceed if email has changed
        if (values.email === user.email) {
          actions.setStatus('Email is unchanged');
          return;
        }

        actions.setStatus(undefined);

        const { data, error } = await updateUser(values, user._id);

        if (data) {
          actions.setStatus('');

          // If we have a message in the response, it means the email change requires verification
          if (data.message) {
            setPendingEmailChange(values.email);
            actions.resetForm({
              values: {
                email: user.email, // Keep the form showing the current email
              },
            });

            emitToast({
              title: 'Verification email sent',
              description: data.message,
              type: 'success',
            });
          } else {
            actions.resetForm({
              values: {
                email: data.email,
              },
            });
            setUser(data);
          }
        } else {
          const { status, errors } = transformApiErrorToFormik(error);

          if (!isEmpty(errors)) {
            actions.setErrors(errors);
          } else {
            actions.setStatus(status);
          }
        }
      }}
    >
      {({ status, dirty, handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
          {pendingEmailChange && (
            <InlineAlert
              type="info"
              className="mb-4 text-sm flex flex-col justify-start items-start space-y-1 text-left"
            >
              <p>
                You have a pending email change to: <strong>{pendingEmailChange}</strong>
              </p>
              <p>Check your email and click the verification link to complete the change.</p>
            </InlineAlert>
          )}

          <FormElement>
            <Label htmlFor="email">Email</Label>
            <TextInput type="email" id="email" name="email" placeholder="Email" required />

            {!user.active && (
              <div className="mt-2 text-xs">
                Your email is not verified. Please verify your email to continue.{' '}
                <Button
                  onClick={() => resendActivationEmailMutation.mutate()}
                  appearance="link"
                  className="text-xs"
                >
                  Resend activation email
                </Button>
              </div>
            )}
          </FormElement>

          <FormElement>{status && status}</FormElement>

          <FormSubmit>
            <FormSubmit.Button>Save</FormSubmit.Button>
            <FormSubmit.State visible={!!dirty} />
          </FormSubmit>
        </Form>
      )}
    </Formik>
  );
};

export default EmailForm;
